import type { ReportDocument } from "../../services/report";
import { approvalLabel, designCheckLabel } from "./reportFormat";
import { ReportWarningNotes } from "./ReportWarningNotes";

// Section 2 of the sheet, document status (Master Prompt §20: purpose and status; the
// design-check state separate from the compliance state): the stored design_check_status in
// the A16 words with its code, the fixed sentence, the reference verification status with the
// unverified marker (GAP-010 rule), the run and approval states. `data-report-draft` carries
// the DRAFT watermark state of A31 (d) from status.is_draft; the watermark itself is the print
// stylesheet's (commit 8). A report warning no other section claims is shown here.

export const DESIGN_CHECK_SENTENCE =
  "This report records a design check. It is not a compliance statement; compliance is " +
  "confirmed only against the applicable source named in the project's design basis.";

export const UNVERIFIED_MARKER =
  "not verified against the applicable source; the governing references carry this status " +
  "until the controlled copy is checked.";

type ReportStatusBlockProps = {
  document: ReportDocument;
};

export function ReportStatusBlock({ document }: ReportStatusBlockProps) {
  const { status } = document;
  const verified = status.reference_verification_status === "VERIFIED";

  return (
    <section
      aria-label="Document status"
      data-report-section="status"
      data-report-draft={status.is_draft ? "true" : "false"}
    >
      <h3>Document status</h3>
      <p data-report-field="document-state">
        <strong>{status.is_draft ? "DRAFT — not approved" : "Approved"}</strong>
      </p>
      <dl>
        <dt>Design check</dt>
        <dd data-report-field="design-check-status" data-result-status={status.design_check_status}>
          {designCheckLabel(status.design_check_status)}
        </dd>
        <dt>References</dt>
        <dd data-report-field="reference-status">
          {status.reference_verification_status}
          {verified ? "" : ` — ${UNVERIFIED_MARKER}`}
        </dd>
        <dt>Run status</dt>
        <dd data-report-field="run-status">{status.run_status}</dd>
        <dt>Approval state</dt>
        <dd data-report-field="approval-status">
          {approvalLabel(status.approval_status)}
          {status.is_immutable ? " (immutable)" : ""}
        </dd>
      </dl>
      <p data-report-field="design-check-sentence">{DESIGN_CHECK_SENTENCE}</p>
      <ReportWarningNotes document={document} section="status" />
    </section>
  );
}

export default ReportStatusBlock;
